import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { GoogleCalendarCard } from "./booking/GoogleCalendarCard";
import { ConnectGateBanner } from "./booking/ConnectGateBanner";
import { WeeklyScheduleEditor } from "./booking/WeeklyScheduleEditor";
import { OverridesPanel } from "./booking/OverridesPanel";
import { adminHref } from "./adminPath";

const todayISO = () => new Date().toISOString().slice(0, 10);

// "Availability settings" orchestrator: the weekly opening hours (WeeklyScheduleEditor)
// and the date-specific edits of them — blocked dates and extra hours (OverridesPanel) —
// under the Google Calendar connection that publishing requires.
//
// The service catalogue is read ONCE here and handed to both: the editor and the
// extra-hours dialog offer its active services as choices, and the extra-hours list
// names the services each row offers (including a since-deactivated one).
export default function AvailabilityAdmin() {
  const t = useT("admin");
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [connection, setConnection] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTemplate = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/availability-template");
      setTemplate(data && data.id ? data : null);
    } catch (e) {
      setTemplate(null);
    }
  }, []);

  const loadExceptions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/availability-exceptions", { params: { date_from: todayISO() } });
      setExceptions(data || []);
    } catch (e) {
      setExceptions([]);
    }
  }, []);

  const loadServices = useCallback(async () => {
    try {
      const { data } = await api.get("/booking/services");
      setServices(Array.isArray(data) ? data : []);
    } catch (e) {
      setServices([]);
    }
  }, []);

  const loadConnection = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/calendar/connection");
      setConnection(data);
    } catch (e) {
      setConnection({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadTemplate(), loadExceptions(), loadConnection(), loadServices()]);
      setLoading(false);
    })();
  }, [loadTemplate, loadExceptions, loadConnection, loadServices]);

  const connected = !!connection?.connected;

  return (
    <div data-testid="admin-availability" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl text-eh-ink">{t("sched.pageTitle")}</h1>
          <p className="text-eh-muted mt-1">{t("sched.pageSubtitle")}</p>
        </div>
        <Link to={adminHref("/admin/calendar")} className="inline-flex items-center gap-1.5 text-sm text-eh-sage-deep hover:underline shrink-0" data-testid="sched-view-calendar-link">
          <CalendarDays size={15} /> {t("sched.viewCalendarLink")}
        </Link>
      </div>

      {/* The connection status is owned by this component: no double fetch of
          /tenant/calendar/connection, and the banner plus WeeklyScheduleEditor's Publish
          button resync immediately after an OAuth return (?gcal=connected), not only after
          a sync. */}
      {/* BUG-028 — the mismatch pill needs somewhere to go. This page has no booking grid
          to filter, so the review happens on the calendar: the pill navigates there rather
          than staying a decorative count (or, before this, a button that did nothing). */}
      <GoogleCalendarCard
        status={connection}
        onRefresh={loadConnection}
        onSynced={loadConnection}
        onShowMismatches={() => navigate(adminHref("/admin/calendar"))}
      />

      {connection && !connected && (
        <ConnectGateBanner showButton={false} configured={connection?.configured !== false} />
      )}

      {!loading && (
        <>
          <WeeklyScheduleEditor template={template} connected={connected} onSaved={loadTemplate} services={services} />
          <OverridesPanel exceptions={exceptions} onChanged={loadExceptions} services={services} />
        </>
      )}
    </div>
  );
}
